import React, { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import UUID from 'uuid-js';
import { store, AppDispatch, RootState, initializeStore, sendFbPixelEvent } from './store';
import { setGlobalParameter } from '../features/globalParameters';
import widgets, { Widget } from './widgets';
import { ParameterInfo, WidgetParamContext } from './hooks';
import './Core.scss';
import I18nProvider from '../i18n/I18nProvider';
import IdProvider from './IdProvider';
import BodySetup from '@src/components/BodySetup/BodySetup';
import { getUtmsFromUrlParams } from '@src/common';
import { ThemeProvider } from '@planne-software/uni/mui/material';
import { lightTheme } from '@planne-software/uni/themes';

const dispatch: AppDispatch = store.dispatch;
const planneWidgetInitializedKey = 'planneWidgetInitialized';
const widgetClassPrefix = 'planne-widget';
const parametersPrefix = 'widget';
const scanIntervalMs = 5000;

export default class {
  private wdw: Window;
  private doc: Document;
  private loaded: boolean;
  private elements: HTMLElement[];
  private scanInterval?: number;
  private url: string;

  constructor(window: Window, document: Document) {
    this.wdw = window;
    this.doc = document;
    this.loaded = false;
    this.elements = [];
    this.url = this.retrieveWidgetUrl();
  }

  loadUtmsFromUrl() {
    const utms = getUtmsFromUrlParams();

    if (Object.keys(utms).length > 0) {
      sessionStorage.setItem('@planne-utms', JSON.stringify(utms));
    }
  }

  init(key: string) {
    dispatch(initializeStore(key, this.doc.documentElement.lang ?? 'pt-br', this.url));

    this.loadUtmsFromUrl();

    this.checkReadyState();

    if (!this.loaded) {
      this.doc.addEventListener('readystatechange', () => this.checkReadyState());
    }
  }

  checkReadyState() {
    if (!this.loaded && ['interactive', 'complete'].includes(this.doc.readyState)) {
      this.loaded = true;

      this.mount();
    }
  }

  mount() {
    this.scanInterval = this.wdw.setInterval(() => this.scan(), scanIntervalMs);
    this.scan();
    this.setupAssets();
  }

  unmount() {
    this.wdw.clearInterval(this.scanInterval);
  }

  scan() {
    const elements = Array.from(this.doc.getElementsByClassName(widgetClassPrefix)) as HTMLElement[];
    const toInitialize = elements.filter((el) => !el.dataset[planneWidgetInitializedKey]);
    toInitialize.forEach((el) => this.setupWidget(el));
  }

  retrieveWidgetUrl() {
    const element = (document.currentScript ?? document.querySelector('script[src$="widget.js"]')) as HTMLScriptElement;
    const origin = element ? new URL(element.src).origin : '';
    return `${origin}/`;
  }

  setupWidget(element: HTMLElement) {
    const widget = widgets.find((widget) => element.classList.contains(`${widgetClassPrefix}-${widget.name}`));
    if (widget === null) {
      return;
    }

    element.dataset[planneWidgetInitializedKey] = 'true';
    this.elements = [...this.elements, element];
    const params = this.extractParams(element, widget!);
    const { component: WidgetComponent } = widget!;

    this.renderComponent(
      <IdProvider id={UUID.create().toString()}>
        <WidgetParamContext.Provider value={params}>
          <WidgetComponent />
        </WidgetParamContext.Provider>
      </IdProvider>,
      element,
    );
  }

  extractParams(el: HTMLElement, widget: Widget): ParameterInfo {
    const values: { [key: string]: string } = {};

    if (
      widget.name === 'product-adder' &&
      !el.dataset[`${parametersPrefix}ProductId`] &&
      !el.dataset[`${parametersPrefix}ProductPackageId`]
    ) {
      console.error(
        `[Planne] ${widget.name} must be used by setting the product id or package id in the data-attribute data-widget-product-id or data-widget-product-package-id in node ${el.tagName}.${el.className}`,
      );
    }

    for (const param of widget.parameters) {
      // eslint-disable-next-line no-useless-escape
      const camelCase = param.name.replace(/(?:\-\w)/g, (m) => m.replace('-', '').toUpperCase());
      const key = `${parametersPrefix}${camelCase.replace(/^\w/, (m) => m.toUpperCase())}`;
      const value = el.dataset[key];

      if (value != null) {
        values[param.name] = value;
      } else if (!param.optional) {
        console.error(
          `[Planne] Couldn't identify ${widget.name} required parameter ${param.name} in node ${el.tagName}.${el.className}`,
        );
      }
    }

    return {
      parameters: widget.parameters,
      values,
    };
  }

  setupAssets() {
    const assetsArea = document.createElement('div');
    document.body.appendChild(assetsArea);

    this.renderComponent(<BodySetup />, assetsArea);
  }

  renderComponent(component: ReactNode, rootEl: HTMLElement) {
    const root = createRoot(rootEl);
    root.render(
      <Provider store={store}>
        <ThemeProvider theme={lightTheme}>
          <I18nProvider>
            <React.StrictMode>{component}</React.StrictMode>
          </I18nProvider>
        </ThemeProvider>
      </Provider>,
    );
  }

  getData(): RootState {
    return store.getState();
  }

  sendFbPixelEvent(pixelId: string, accessTokenPath: string, eventData: any) {
    dispatch(sendFbPixelEvent(pixelId, accessTokenPath, eventData));
  }

  callMethod(...params: any[]) {
    const [typeStr, ...args] = params;

    switch (typeStr) {
      case 'init':
        const [apiKey] = args;
        this.init(apiKey);
        break;
      case 'parameter': {
        const [key, value] = args;
        dispatch(setGlobalParameter(key, value));
        break;
      }
    }
  }
}
